/**
 * Configuration validation and management
 */

import type { AlterConnectConfig } from "../types";

/**
 * Canonical Alter Connect host.
 *
 * Single source of truth for the widget's default host. `alter-connect.ts`
 * uses it as the constructor fallback; `config.ts` and `oauth/handler.ts`
 * surface it in error messages so operators always see the same example
 * URL no matter which validation layer flags their misconfig.
 */
export const DEFAULT_ALTER_HOST = "https://backend.alterauth.com";

/**
 * Internal config used by the SDK
 */
interface FullConfig {
  debug: boolean;
}

/**
 * Validate configuration.
 *
 * `baseURL` is reserved in the public type but is not accepted at runtime.
 */
export function validateConfig(config: AlterConnectConfig): void {
  if (config.baseURL !== undefined) {
    // Keep the message free of the supplied value: a caller can place secrets
    // in this reserved field and errors flow into tracking integrations.
    throw new Error(
      "AlterConnectConfig.baseURL is reserved and not yet supported. Omit the field to use the production Alter host.",
    );
  }
}

/**
 * Merge user config with defaults
 */
export function mergeConfig(config: AlterConnectConfig): FullConfig {
  return {
    debug: config.debug ?? false,
  };
}

/**
 * Debug logging helper
 */
export function debugLog(config: FullConfig, ...args: unknown[]): void {
  if (config.debug) {
    // Deliberate console.log: this IS the debug logger, and it only fires
    // when the integrator explicitly opts in via ``config.debug``.
    // eslint-disable-next-line no-console
    console.log("[Alter Connect]", ...args);
  }
}

/**
 * Export FullConfig type for use in other modules
 */
export type { FullConfig };
